import React, { useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { finApi, money } from "@/lib/financeApi";

const BADGE = {
  received: ["Received", "bg-emerald-500/15 text-emerald-300 border-emerald-400/30"],
  waiting: ["Waiting", "bg-amber-400/15 text-amber-200 border-amber-300/30"],
  self_pay: ["Self-pay", "bg-sky-500/15 text-sky-300 border-sky-400/30"],
};
const tid = (x) => `${x.invoice_id || "self"}-${x.paycheck_ids[0] || x.resident_id}`;

function EditAmount({ x, field, onSaved }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState(String(x[field]));
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const id = `org-ledger-${field === "rent" ? "rent" : "cm"}-${tid(x)}`;
  const save = () => {
    setBusy(true);
    finApi.post("/org-ledger/adjust", { key: x.key, [field]: Number(v), reason: why })
      .then((r) => { toast.success(r.note || "Saved · the resident's paycheck split was updated too"); setOpen(false); onSaved(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't save")).finally(() => setBusy(false));
  };
  if (!open) {
    return (
      <span className="inline-flex items-center gap-1 justify-end">
        <span data-testid={id} className="font-mono">{money(x[field])}</span>
        <button type="button" onClick={(e) => { e.stopPropagation(); setV(String(x[field])); setOpen(true); }} data-testid={`${id}-edit`} title="Adjust"
          className="text-nex-muted hover:text-nex-gold transition-colors"><Pencil className="h-3 w-3" /></button>
      </span>);
  }
  return (
    <span className="inline-flex flex-col gap-1 items-end" onClick={(e) => e.stopPropagation()}>
      <input type="number" min="0" step="0.01" value={v} onChange={(e) => setV(e.target.value)} data-testid={`${id}-input`} className="w-20 bg-black/50 border border-nex-gold/50 px-1.5 py-0.5 text-right font-mono text-xs text-nex-silver" />
      <input value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Why (e.g. check short)" data-testid={`${id}-reason`} className="w-36 bg-black/50 border border-white/15 px-1.5 py-0.5 text-[11px] text-nex-silver" />
      <span className="flex gap-1">
        <button type="button" disabled={busy || !why.trim() || v === ""} onClick={save} data-testid={`${id}-save`} className="p-1 border border-emerald-400/40 text-emerald-300 disabled:opacity-40"><Check className="h-3 w-3" /></button>
        <button type="button" onClick={() => setOpen(false)} data-testid={`${id}-cancel`} className="p-1 border border-white/15 text-nex-muted"><X className="h-3 w-3" /></button>
      </span>
    </span>);
}

export function OrgLedgerResidents({ rows, onSaved }) {
  if (!rows.length) return <p className="px-4 py-3 text-xs text-nex-muted">No residents in this week.</p>;
  return (
    <table className="w-full text-left text-xs">
      <thead><tr className="font-mono text-[9px] tracking-[0.14em] uppercase text-nex-muted">
        {["Resident", "Invoice", "Paycheck", "Rent", "PACT Fee", "Distributed", "Employer payment"].map((h, i) => <th key={h} className={`px-3 py-1.5 font-normal ${i > 1 && i < 6 ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
      <tbody>{rows.map((x) => (
        <tr key={x.key} data-testid={`org-ledger-res-${tid(x)}`} className="border-t border-white/5 align-top">
          <td className="px-3 py-1.5 text-nex-silver">{x.name || "Resident"}{x.adjusted && <div className="text-[10px] text-nex-gold" title={x.adjust_note}>edited · {x.adjust_note}</div>}
            {x.distributed < -0.005 && <div data-testid={`org-ledger-short-${tid(x)}`} className="text-[10px] text-amber-300">Logged split takes more than gross · fix the paycheck</div>}</td>
          <td className="px-3 py-1.5 text-nex-muted">{x.invoice_number ? `#${x.invoice_number}` : "Own paycheck"} · {x.vendor || "—"}</td>
          <td className="px-3 py-1.5 text-right font-mono">{x.has_paycheck ? money(x.gross) : <span className="text-nex-muted">none yet</span>}</td>
          <td className="px-3 py-1.5 text-right"><EditAmount x={x} field="rent" onSaved={onSaved} /></td>
          <td className="px-3 py-1.5 text-right"><EditAmount x={x} field="case_management" onSaved={onSaved} /></td>
          <td className="px-3 py-1.5 text-right font-mono">{money(x.distributed)}</td>
          <td className="px-3 py-1.5"><span data-testid={`org-ledger-res-status-${tid(x)}`} className={`inline-block px-2 py-0.5 rounded-sm border font-mono text-[9px] uppercase tracking-[0.12em] ${BADGE[x.status][1]}`}>{BADGE[x.status][0]}</span></td>
        </tr>))}</tbody>
    </table>
  );
}

export function LandlordSummary({ l, label }) {
  const parts = [["rent_owed", "Total rent owed to landlord"], ["paid", "Already paid to landlord"], ["outstanding", "Outstanding · TEA still owes"]];
  return (
    <div data-testid="org-ledger-landlord" className="nds-card p-4 space-y-3">
      <div className="nds-label">Landlord payables · {label}</div>
      <div className="flex flex-wrap items-end gap-3 sm:gap-5">
        {parts.map(([k, t], i) => (
          <React.Fragment key={k}>
            {i > 0 && <span className="text-nex-muted font-mono text-lg pb-1">{i === 1 ? "−" : "="}</span>}
            <div>
              <div className="text-[10px] text-nex-muted">{t}</div>
              <div data-testid={`org-ledger-landlord-${k}`} className={`font-mono text-xl ${k === "outstanding" ? (l[k] < 0 ? "text-emerald-300" : "text-nex-goldbright") : "text-nex-silver"}`}>{money(l[k])}</div>
            </div>
          </React.Fragment>))}
      </div>
      <p className="text-[11px] text-nex-muted">{l.outstanding > 0 ? `Write the landlord a check for ${money(l.outstanding)}.` : l.outstanding < 0 ? `TEA has paid ${money(-l.outstanding)} more than the rent counted here.` : "Nothing outstanding."} Rent counts from received invoices and self-pay paychecks.</p>
    </div>
  );
}
